import { AlertTriangle, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRouteError, useNavigate } from "react-router";
import * as Sentry from "@sentry/react";

export default function ErrorPage({ error, resetError }: { error: Error; resetError: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center">
      <div className="flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-red-100 dark:bg-red-900/20">
        <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
      </div>
      <h2 className="mb-2 text-xl font-semibold text-gray-900 dark:text-gray-100">
        Something went wrong
      </h2>
      <p className="mb-6 text-gray-600 dark:text-gray-400 max-w-md">
        We've encountered an unexpected error. Our team has been notified and is working on a fix.
      </p>
      {resetError && (
        <Button onClick={resetError} variant="outline" className="gap-2">
          <RotateCcw className="w-4 h-4" />
          Try again
        </Button>
      )}
      {error && import.meta.env.DEV && (
        <details className="mt-6 p-4 bg-gray-100 dark:bg-gray-800 rounded text-left max-w-2xl">
          <summary className="cursor-pointer font-medium mb-2">Error Details</summary>
          <pre className="text-sm text-red-600 dark:text-red-400 whitespace-pre-wrap">
            {error.message}
            {"\n\n"}
            {error.stack}
          </pre>
        </details>
      )}
    </div>
  );
}

export function RouteErrorElement() {
  const error = useRouteError();
  const navigate = useNavigate();

  const handleResetError = () => {
    navigate("/");
  };

  // Convert the error to a proper Error object
  let errorObj: Error;
  if (error instanceof Error) {
    errorObj = error;
  } else if (typeof error === "string") {
    errorObj = new Error(error);
  } else if (error && typeof error === "object" && "message" in error) {
    errorObj = new Error((error as any).message);
  } else if (error && typeof error === "object" && "error" in error) {
    errorObj = new Error((error as any).error.message);
  } else {
    errorObj = new Error("An unknown error occurred");
  }

  // Report error to Sentry
  Sentry.captureException(errorObj, {
    tags: {
      errorBoundary: "RouteErrorElement",
      location: "router",
    },
    extra: {
      originalError: error,
      timestamp: new Date().toISOString(),
    },
  });

  return <ErrorPage error={errorObj} resetError={handleResetError} />;
}
